import type { Registry as Services } from '@ember/service';
import type Store from '@ember-data/store';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { tKey } from 'ember-intl';
import type { Day } from 'lumen/models';
import {
  DATE_AGGREGATION,
  MI_DEFAULT_DATE_FORMAT,
} from 'lumen/utils/constants/general-constants';
import {
  FIXED_EXTRACT_DATE_LATEST,
  GRID_HOTELS_TYPE_OWN_COMPSET,
  GRID_HOTELS_TYPE_OWN_HOTEL,
  GRID_HOTELS_TYPE_SMART_COMPSET,
} from 'lumen/utils/constants/market-insight-constants';

export interface MarketInsightHotel {
  formattedStars: string;
  isOwnHotel: boolean;
  lat: number;
  lng: number;
  name: string;
  our_hotel_id: string;
  review_score: number;
  similarity_score: number;
  stars: number;
  types: number[];
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function checkIsOwnHotel(hotel: MarketInsightHotel) {
  return hotel.types.indexOf(GRID_HOTELS_TYPE_OWN_HOTEL) !== -1;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function checkIsPrimaryCompset(hotel: MarketInsightHotel) {
  return hotel.types.indexOf(GRID_HOTELS_TYPE_OWN_COMPSET) !== -1;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function checkIsSmartCompset(hotel: MarketInsightHotel) {
  return hotel.types.indexOf(GRID_HOTELS_TYPE_SMART_COMPSET) !== -1;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function checkIsOwnHotelOrSmartCompset(hotel: MarketInsightHotel) {
  return checkIsOwnHotel(hotel) || checkIsSmartCompset(hotel);
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getCompsetIndicatorCssClass(hotel: MarketInsightHotel) {
  if (checkIsPrimaryCompset(hotel) && checkIsSmartCompset(hotel)) {
    return 'own-and-smart-compset';
  }

  if (checkIsPrimaryCompset(hotel)) {
    return 'own-compset';
  }

  if (checkIsSmartCompset(hotel)) {
    return 'smart-compset';
  }

  return 'own-hotel';
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function fetchEventsByVisibilityFlag(
  dayModel: Day,
  showAllEvents: boolean,
) {
  if (showAllEvents) {
    return dayModel.eventsExceptRejected;
  }

  return dayModel.calendarEvents;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function xAxisLabelFormatterForOverview(
  xValue: number,
  isDailyOverview: boolean,
  intl: Services['intl'],
) {
  const date = dayjs.utc(xValue);

  if (isDailyOverview) {
    return date.format('ll');
  }

  // If weekly view, reflect it in the label
  // Note: don't remove toString, as it fixed bug when opening OTB chart
  return intl
    .t(tKey('general.week_of'), {
      date: date.format(MI_DEFAULT_DATE_FORMAT),
    })
    .toString();
}

// Custom tooltip positioner is required to still have correct tooltip positioning with the tooltip "outside" option enabled
// Note: labelWidth = width of the tooltip, labelHeight = height of the tooltip
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function tooltipPositionerForOverview(
  labelWidth: number,
  labelHeight: number,
  point: {
    plotX: number;
  },
  isWeeklyOverview: boolean,
  verticalChartIndex: number,
  isMarketInsightFull: boolean,
) {
  // In case of outliers with high values the plotY value is very small and causes annoying positioning of the tooltip.
  // Tried to add a sane safety value in that case
  // Note: commented this out, because it felt "safer" to just lock the y value on plotY for now. Will leave as comment if we ever want to investigate this further
  // const plotYPlusSafety = Math.max(110, point.plotY);

  // Extra spacing to try and position the tooltip left of the hovered one.
  // Set the value of this number after testing multiple day/week selections on different screensizes (the column widths in the chart have an impact here)
  // E.g.: when the value is too low, this will work on wider screens... but you'll run into trouble on smaller screensizes where the column bars have a smaller width
  const extraXaxisSpacingBetweenHoveredBarAndTooltip = 160;

  // Note: if the tooltip height is ever updated (or the vertical position of the demand chart changes), these values need to be tweaked again!
  // Extra spacing to position the tooltips better in the weekly overview without losing the fixed vertical alignment
  let yAxisSpacing = 175;

  if (!isMarketInsightFull) {
    // Extra correction for the prism-banner that's in the overview.hbs of MI Light
    yAxisSpacing += 112;
  }

  if (verticalChartIndex === 0) {
    // Weekly view is smaller than daily view for the demand chart: position it a bit deeper
    yAxisSpacing += isWeeklyOverview ? 100 : 0;
  } else {
    // Add extra vertical spacing based on the vertical position of the chart: top chart starts with index 0, bottom chart has the highest index
    yAxisSpacing += verticalChartIndex * 600;
  }

  // We need to correct the vertical position when scrolling down the page so the tooltip doesn't scroll down as well
  const overviewScrollTop =
    document.getElementById('main-mi-content-container')?.scrollTop ?? 0;
  const maxCorrection = 120; // If this value would be lower, the tooltip would disappear behind the blue bar when the page is scrolled down
  const yAxisCorrection = Math.min(
    overviewScrollTop,
    yAxisSpacing - maxCorrection,
  ); // Ranges from 0 (not scrolled down) to maxCorrection (based on scrollTop position)

  return {
    x:
      point.plotX +
      labelWidth / 2 -
      extraXaxisSpacingBetweenHoveredBarAndTooltip,
    y: yAxisSpacing - yAxisCorrection,
  };
}

/**
 * Generic helper function to easily peek summaries in the store
 * @param {string}    modelName    : name of the model to peek
 * @param {string}    typePrefix   : certain summaries need to be selected by type (e.g.: searchsummaries - Skyscanner & Travelport). This prefix already contains ',' if present
 * @param {string}    selectedDay  : string representing the current selected day (in YYYY-MM-DD format)
 * @param {string or dayjs}    currentExtractDate :  will be a dayjs object representing the extract date, or a string indicating "latest"
 * @return null or the summary result from the store
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getSummaryForSelectedDayAndExtractDate(
  store: Store,
  modelName: string,
  typePrefix = '',
  selectedDay: string,
  currentExtractDate: Dayjs | typeof FIXED_EXTRACT_DATE_LATEST,
  dateAggregation = null,
) {
  let currentSummary = null;

  if (currentExtractDate) {
    let recordId = `${typePrefix}${selectedDay}`;
    recordId +=
      currentExtractDate === FIXED_EXTRACT_DATE_LATEST
        ? `,${FIXED_EXTRACT_DATE_LATEST}`
        : `,${currentExtractDate.format('YYYY-MM-DD')}`;
    // Only append the date aggregation for weeks (API is backward compatible)
    recordId +=
      dateAggregation && dateAggregation === DATE_AGGREGATION.WEEK
        ? // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          `,${dateAggregation}`
        : '';

    currentSummary = store.peekRecord(modelName, recordId);
  }

  return currentSummary;
}
